/**
 * The token *contract* — every custom-property name the design system promises to define, and
 * nothing about what any of them is worth.
 *
 * ## Why the names live here and the values live in `packages/ui`
 *
 * The design (`design.md` → "Design system") puts the Tailwind preset in `packages/config` and
 * has it consume the token layer in `packages/ui`. Read literally that inverts the package graph:
 * `config --> ui` in the design's own dependency diagram means config is *consumed by* ui, and
 * "dependency direction is strictly one-way". `packages/config` is a devDependency of every
 * workspace, which is the same reason it cannot import `@apuestapr/env`.
 *
 * So the split is by kind rather than by file. `packages/config` owns the contract: the token
 * names, the utility families, and the preset that maps each name to
 * `hsl(var(--name) / <alpha-value>)`. `packages/ui` owns the values: the measured brand palette,
 * the two theme blocks, the emitted CSS, and the contrast lint. `ui` imports `config`, never the
 * reverse, and the link is enforced rather than trusted — `packages/ui` asserts that both theme
 * blocks declare exactly the key set named here, so adding a name to the preset without giving it
 * a value in both themes fails CI.
 *
 * ## Format
 *
 * Colours are space-separated HSL triplets (`0 97.3% 43.5%`), never complete `hsl(...)` strings.
 * That is what makes `bg-primary/50` render at 50%: Tailwind substitutes the alpha into
 * `hsl(var(--primary) / <alpha-value>)`, and it can only do that if the variable holds the
 * channels alone. The dashboard being replaced stores `--primary: hsl(207, 90%, 54%)`, so every
 * opacity modifier in it silently renders at full opacity. Requirement 6.1 is that defect.
 */

/**
 * Brand tokens, prefixed `--apr-*`. Identical in both themes — a brand colour does not change
 * when the theme does. Named exactly as the design's palette table names them.
 */
export const brandTokenNames = /** @type {const} */ ([
  'apr-red',
  'apr-redDeep',
  'apr-redSoft',
  'apr-navy',
  'apr-navyDeep',
  'apr-navyAbyss',
  'apr-navyLift',
  'apr-white',
  'apr-gold',
]);

/**
 * Semantic tokens. Every one of these derives from a brand token, never from an independent
 * literal, and every one is defined in both themes so a primitive is theme-agnostic
 * (requirement 7.6). The dashboard's separate `--apuesta-*` palette is dropped entirely.
 *
 * `DEFAULT`-and-`foreground` pairs become `bg-card` / `text-card-foreground` in Tailwind, which
 * is where the `-foreground` naming comes from: it is the ink the surface is contrast-checked
 * against.
 */
export const semanticTokenNames = /** @type {const} */ ([
  'background',
  'foreground',
  'card',
  'card-foreground',
  'popover',
  'popover-foreground',
  'primary',
  'primary-foreground',
  'secondary',
  'secondary-foreground',
  'muted',
  'muted-foreground',
  'accent',
  'accent-foreground',
  'destructive',
  'destructive-foreground',
  'border',
  'input',
  'ring',
]);

/** Every colour custom property, brand and semantic. */
export const colorTokenNames = /** @type {const} */ ([...brandTokenNames, ...semanticTokenNames]);

/**
 * Elevation is one shared ramp rather than a per-component invention. Themed, because a shadow
 * that reads as depth on white reads as nothing on navy — the dark ramp leans on a lighter top
 * edge instead of a darker drop.
 *
 * This replaces the situation the prior audit described, where `PromoCard3D` stacked mouse-tilt, a
 * gradient overlay, a separate blurred glow layer, and per-element `translateZ` while
 * `LocationCard` and `PromoCard` each did something else.
 */
export const elevationTokenNames = /** @type {const} */ ([
  'elevation-flat',
  'elevation-raised',
  'elevation-overlay',
  'elevation-modal',
]);

/** Every custom property that differs between the two themes. */
export const themedTokenNames = /** @type {const} */ ([...colorTokenNames, ...elevationTokenNames]);

/**
 * Custom properties that are the same under both themes, declared once on `:root`. Radius,
 * duration, and easing are geometry and timing, not colour, so theming them would be drift
 * dressed up as a feature.
 */
export const rootTokenNames = /** @type {const} */ ([
  'radius',
  'duration-instant',
  'duration-fast',
  'duration-base',
  'duration-slow',
  'ease-standard',
  'ease-entrance',
  'ease-exit',
  // `--family-*` rather than `--font-*`, so nothing reads as the `font-display` CSS descriptor
  // that the same task sets to `swap`.
  'family-display',
  'family-body',
]);
